import { Card, Title3, makeStyles, tokens, Text } from '@fluentui/react-components';
import { Warning20Regular, ErrorCircle20Regular } from '@fluentui/react-icons';
import type { DashboardAlert } from '../../../hooks/useDashboard';

const intentOrder: Record<string, number> = { error: 0, warning: 1, info: 2, success: 3 };

const useStyles = makeStyles({
  card: {
    maxHeight: '600px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    overflowY: 'auto',
    flex: 1,
    paddingTop: tokens.spacingVerticalS,
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  icon: { flexShrink: 0, marginTop: '3px' },
  text: { flex: 1, minWidth: 0 },
});

export const AlertsPanel = ({ alerts }: { alerts: DashboardAlert[] }) => {
  const styles = useStyles();
  const sorted = [...alerts].sort((a, b) => {
    // Errors first, then sort by date (soonest renewal first)
    const intentDiff = (intentOrder[a.intent] ?? 9) - (intentOrder[b.intent] ?? 9);
    if (intentDiff !== 0) return intentDiff;
    return (a.sortDate ?? '9999').localeCompare(b.sortDate ?? '9999');
  });
  return (
    <Card className={styles.card}>
      <Title3 style={{ flexShrink: 0 }}>Operational alerts ({sorted.length})</Title3>
      {sorted.length === 0
        ? <Text style={{ color: tokens.colorNeutralForeground3 }}>No active alert.</Text>
        : (
          <div className={styles.list}>
            {sorted.map((alert) => (
              <div key={`${alert.title}-${alert.description}`} className={styles.item}>
                <span className={styles.icon}>
                  {alert.intent === 'error'
                    ? <ErrorCircle20Regular color={tokens.colorPaletteRedForeground1} />
                    : <Warning20Regular color={tokens.colorPaletteYellowForeground1} />
                  }
                </span>
                <span className={styles.text}>
                  <Text weight="semibold" size={400} block>{alert.title}</Text>
                  <Text as="p" size={300} style={{ margin: '4px 0 0', color: tokens.colorNeutralForeground2 }}>{alert.description}</Text>
                </span>
              </div>
            ))}
          </div>
        )
      }
    </Card>
  );
};
